<template>
  <main class="scanning">
    <div
      class="scanning__content"
      :class="{ 'scanning__content--dragging': dragging }"
      @dragenter.prevent="dragging = true"
      @dragover.prevent="dragging = true"
      @dragleave.self.prevent="dragging = false"
      @drop.prevent="dropFiles"
    >
      <input
        ref="fileInput"
        class="scanning__file-input"
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        multiple
        :disabled="processing || readingFiles"
        @change="selectFiles"
      />

      <div
        class="scanning__dropzone"
        role="button"
        tabindex="0"
        @click="openFilePicker"
        @keydown.enter.prevent="openFilePicker"
        @keydown.space.prevent="openFilePicker"
      >
        <img src="../assets/img/scan.svg" alt="" />
        <h1>Кликнете или плъзнете файлове тук</h1>
        <p>PDF, JPEG, PNG или WebP. До 15 MB на файл и 50 MB общ размер.</p>
        <p v-if="hasLockedLocation">Проект/Обект/Подобект са взети от отчета.</p>
        <p v-else>Проектът се избира предварително.</p>
      </div>

      <section class="scanning__inputs">
        <BaseField
          field-class="scanning__input"
          label="Проект"
          icon="N"
          placeholder="Проект"
          :value="selectedCompanyName"
          :options="companies"
          option-label="companyName"
          :disabled="hasLockedLocation"
          :clearable="!hasLockedLocation"
          :selected="Boolean(inquiry.companyId)"
          @select="selectCompany"
          @clear="resetCompany"
        />
        <BaseField
          field-class="scanning__input"
          label="Обект"
          icon="N"
          placeholder="Обект"
          :value="selectedProjectName"
          :options="projects"
          option-label="projectName"
          :disabled="hasLockedLocation || !inquiry.companyId"
          :clearable="!hasLockedLocation"
          :selected="Boolean(inquiry.projectId)"
          @select="selectProject"
          @clear="resetProject"
        />
        <BaseField
          field-class="scanning__input"
          label="Подобект"
          icon="N"
          placeholder="Подобект"
          :value="selectedObjectName"
          :options="objects"
          option-label="objectName"
          :disabled="hasLockedLocation || !inquiry.projectId"
          :clearable="!hasLockedLocation"
          :selected="Boolean(inquiry.objectId)"
          @select="selectObject"
          @clear="resetObject"
        />
      </section>

      <p v-if="selectionError" class="scanning__error">{{ selectionError }}</p>

      <section v-if="files.length" class="scanning__files">
        <section
          v-for="section in fileSections"
          :key="section.key"
          class="scanning-file-group"
          :class="`scanning-file-group--${section.key}`"
        >
          <h2 class="scanning-file-group__title">
            {{ section.title }}
            <span>{{ section.files.length }}</span>
          </h2>

          <article
            v-for="entry in section.files"
            :key="entry.id"
            class="scanning-file"
            :class="`scanning-file--${entry.status}`"
            :title="entryMessage(entry)"
          >
            <div class="scanning-file__header">
              <div>
                <strong>{{ entry.file.name }}</strong>
                <span
                  v-if="
                    (entry.status === 'rejected' || entry.status === 'error') &&
                    entryMessage(entry)
                  "
                  class="scanning-file__error"
                >
                  {{ entryMessage(entry) }}
                </span>
              </div>
              <button
                v-if="!processing"
                type="button"
                class="scanning-file__remove"
                aria-label="Премахни файла"
                @click="removeFile(entry.id)"
              >
                ×
              </button>
            </div>
            <button
              v-if="canAddExistingInvoice(entry)"
              type="button"
              class="btn btn-new scanning-file__report-button"
              @click="addExistingInvoice(entry)"
            >
              Отчети данните от фактурата
            </button>
          </article>
        </section>
      </section>

      <button
        class="btn btn-new"
        :disabled="readingFiles || processing"
        @click="scanFiles"
      >
        {{
          processing
            ? `Обработване на фактура ${processingIndex} от ${processingTotal}`
            : readingFiles
              ? 'Подготовка на файловете...'
            : 'Сканирай'
        }}
      </button>
    </div>
  </main>
</template>

<script>
import BaseField from "./shared/BaseField";

const MAX_FILE_SIZE = 15 * 1024 * 1024;
const MAX_TOTAL_SIZE = 50 * 1024 * 1024;
const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
]);

function readFileEntry(entry) {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

function readDirectoryBatch(reader) {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}

async function filesFromEntry(entry) {
  if (entry.isFile) return [await readFileEntry(entry)];
  if (!entry.isDirectory) return [];

  const reader = entry.createReader();
  const entries = [];
  let batch;

  do {
    batch = await readDirectoryBatch(reader);
    entries.push(...batch);
  } while (batch.length);

  const nestedFiles = await Promise.all(entries.map(filesFromEntry));
  return nestedFiles.reduce((files, nested) => files.concat(nested), []);
}

async function getDroppedFiles(dataTransfer) {
  const items = Array.from((dataTransfer && dataTransfer.items) || []).filter(
    item => item.kind === "file"
  );
  const entries = items
    .map(item =>
      typeof item.webkitGetAsEntry === "function" ? item.webkitGetAsEntry() : null
    )
    .filter(Boolean);

  if (!entries.length) return Array.from((dataTransfer && dataTransfer.files) || []);

  const nestedFiles = await Promise.all(entries.map(filesFromEntry));
  return nestedFiles.reduce((files, nested) => files.concat(nested), []);
}

async function createStableFile(file) {
  const contents = await file.arrayBuffer();

  return new File([contents], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

export default {
  components: {
    BaseField,
  },
  props: {
    lockedLocation: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      companies: [],
      projects: [],
      objects: [],
      inquiry: {
        companyId: "",
        projectId: "",
        objectId: "",
      },
      files: [],
      dragging: false,
      readingFiles: false,
      processing: false,
      processingIndex: 0,
      processingTotal: 0,
      selectionError: "",
    };
  },
  computed: {
    hasLockedLocation() {
      return Boolean(
        this.lockedLocation &&
          this.lockedLocation.company?._id &&
          this.lockedLocation.project?._id &&
          this.lockedLocation.object?._id
      );
    },
    selectedCompanyName() {
      if (this.hasLockedLocation) return this.lockedLocation.company.companyName;
      const company = this.companies.find(
        item => String(item._id) === String(this.inquiry.companyId)
      );
      return company ? company.companyName : "";
    },
    selectedProjectName() {
      if (this.hasLockedLocation) return this.lockedLocation.project.projectName;
      const project = this.projects.find(
        item => String(item._id) === String(this.inquiry.projectId)
      );
      return project ? project.projectName : "";
    },
    selectedObjectName() {
      if (this.hasLockedLocation) return this.lockedLocation.object.objectName;
      const object = this.objects.find(
        item => String(item._id) === String(this.inquiry.objectId)
      );
      return object ? object.objectName : "";
    },
    canScan() {
      return Boolean(
        this.inquiry.objectId &&
          this.files.some(entry => entry.status === "pending" || entry.status === "error") &&
          !this.readingFiles &&
          !this.processing
      );
    },
    fileSections() {
      return [
        {
          key: "accepted",
          title: "Успешни фактури",
          files: this.files.filter(entry => entry.status === "accepted"),
        },
        {
          key: "failed",
          title: "Неуспешни фактури",
          files: this.files.filter(
            entry => entry.status === "rejected" || entry.status === "error"
          ),
        },
        {
          key: "pending",
          title: "За обработка",
          files: this.files.filter(
            entry => entry.status === "pending" || entry.status === "processing"
          ),
        },
      ].filter(section => section.files.length);
    },
  },
  watch: {
    lockedLocation: {
      deep: true,
      immediate: true,
      handler() {
        if (!this.hasLockedLocation) return;
        this.inquiry.companyId = this.lockedLocation.company._id;
        this.inquiry.projectId = this.lockedLocation.project._id;
        this.inquiry.objectId = this.lockedLocation.object._id;
      },
    },
  },
  mounted() {
    if (!this.hasLockedLocation) this.getCompanies();
  },
  methods: {
    async getCompanies() {
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_BASE_URI}/company/active`
        );
        this.companies = response.data;
        if (this.companies.length === 1) this.selectCompany(this.companies[0]);
      } catch (error) {
        this.selectionError = "Неуспешно зареждане на проектите.";
      }
    },
    selectCompany(company) {
      this.inquiry.companyId = company._id;
      this.projects = company && company.projects ? company.projects : [];
      this.resetProject();
      if (this.projects.length === 1) this.selectProject(this.projects[0]);
    },
    selectProject(project) {
      this.inquiry.projectId = project._id;
      this.objects = project && project.objects ? project.objects : [];
      this.resetObject();
      if (this.objects.length === 1) this.selectObject(this.objects[0]);
    },
    selectObject(object) {
      this.inquiry.objectId = object._id;
    },
    resetCompany() {
      this.inquiry.companyId = "";
      this.projects = [];
      this.resetProject();
    },
    resetProject() {
      this.inquiry.objectId = "";
      this.inquiry.projectId = "";
      this.objects = [];
    },
    resetObject() {
      this.inquiry.objectId = "";
    },
    openFilePicker() {
      if (!this.processing && !this.readingFiles) this.$refs.fileInput.click();
    },
    async addFiles(fileList) {
      if (this.readingFiles) return;

      this.selectionError = "";
      this.readingFiles = true;
      let totalSize = this.files.reduce((sum, entry) => sum + entry.file.size, 0);

      try {
        for (const file of Array.from(fileList || [])) {
          const id = `${file.name}-${file.size}-${file.lastModified}`;

          if (this.files.some(entry => entry.id === id)) continue;

          if (!ALLOWED_MIME_TYPES.has(file.type)) {
            this.selectionError = `Неподдържан формат: ${file.name}`;
            continue;
          }

          if (file.size > MAX_FILE_SIZE) {
            this.selectionError = `${file.name} е по-голям от 15 MB.`;
            continue;
          }

          if (totalSize + file.size > MAX_TOTAL_SIZE) {
            this.selectionError =
              "Общият размер на избраните файлове не може да надвишава 50 MB.";
            break;
          }

          try {
            const stableFile = await createStableFile(file);
            this.files.push({
              id,
              file: stableFile,
              status: "pending",
              result: null,
              error: "",
            });
            totalSize += stableFile.size;
          } catch (error) {
            console.error("Unable to copy selected file", error);
            this.selectionError = `${file.name} не може да бъде прочетен. Запазете го локално и опитайте отново.`;
          }
        }
      } finally {
        this.readingFiles = false;
      }
    },
    async selectFiles(event) {
      const files = Array.from(event.target.files || []);

      try {
        await this.addFiles(files);
      } finally {
        event.target.value = "";
      }
    },
    async dropFiles(event) {
      this.dragging = false;
      if (this.processing || this.readingFiles) return;

      try {
        await this.addFiles(await getDroppedFiles(event.dataTransfer));
      } catch (error) {
        console.error("Unable to read dropped files", error);
        this.selectionError =
          "Някои от файловете не могат да бъдат прочетени. Запазете ги локално и опитайте отново.";
      }
    },
    removeFile(id) {
      if (!this.processing && !this.readingFiles) {
        this.files = this.files.filter(entry => entry.id !== id);
      }
    },
    responseError(error) {
      if (!error.response) {
        return "Заявката беше прекъсната, преди сървърът да върне отговор. Опитайте отново.";
      }

      return (
        (error.response.data && error.response.data.message) ||
        error.message ||
        "Неуспешно разчитане на фактурата."
      );
    },
    entryMessage(entry) {
      return (
        entry.error ||
        (entry.result && entry.result.validation && entry.result.validation.message) ||
        ""
      );
    },
    canAddExistingInvoice(entry) {
      return Boolean(
        this.hasLockedLocation &&
          entry.result &&
          entry.result.validation &&
          entry.result.validation.code === "DUPLICATE_INVOICE" &&
          entry.result.invoice &&
          entry.result.savedInvoice &&
          entry.result.savedInvoice._id
      );
    },
    addExistingInvoice(entry) {
      this.$emit("invoice-accepted", entry.result);
      entry.status = "accepted";
    },
    async processEntry(entry) {
      const formData = new FormData();
      formData.append("file", entry.file);
      formData.append("companyId", this.inquiry.companyId);
      formData.append("projectId", this.inquiry.projectId);
      formData.append("objectId", this.inquiry.objectId);

      entry.status = "processing";
      entry.result = null;
      entry.error = "";

      try {
        const response = await this.$http.post(
          `${process.env.VUE_APP_BASE_URI}/invoice-reader/uploads`,
          formData
        );

        entry.result = response.data;
        entry.status = response.data.accepted ? "accepted" : "rejected";
        if (response.data.accepted) {
          this.$emit("invoice-accepted", response.data);
        }
      } catch (error) {
        const response = error.response && error.response.data;

        if (
          error.response &&
          (error.response.status === 409 || error.response.status === 422) &&
          response &&
          response.invoice
        ) {
          entry.result = response;
          entry.status = "rejected";
          return;
        }

        entry.status = "error";
        entry.error = this.responseError(error);
      }
    },
    async scanFiles() {
      if (!this.files.length) {
        this.selectionError = "Не сте качили файл";
        return;
      }

      if (!this.inquiry.objectId) {
        this.selectionError = "Изберете проект, обект и подобект.";
        return;
      }

      if (!this.canScan) return;

      this.selectionError = "";

      const entries = this.files.filter(
        entry => entry.status === "pending" || entry.status === "error"
      );

      this.processing = true;
      this.$emit("processing-change", true);
      this.processingIndex = 0;
      this.processingTotal = entries.length;

      try {
        for (const [index, entry] of entries.entries()) {
          this.processingIndex = index + 1;
          await this.processEntry(entry);
        }
      } finally {
        this.processing = false;
        this.$emit("processing-change", false);
        this.processingIndex = 0;
        this.processingTotal = 0;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.scanning {
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  max-width: 1050px;
  padding: 20px;
  width: 100%;

  &__content {
    align-items: center;
    background: #fafafc;
    border: 1px dashed #858585;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px 8%;
    transition: all 0.3s ease-out;
    width: 100%;

    &--dragging {
      background: #eef6ff;
      border-color: #0078d7;
    }

    h1 {
      font-size: 16px;
      font-weight: 700;
    }

    p {
      color: #767c82;
      margin: 0;
    }
  }

  &__file-input {
    display: none;
  }

  &__dropzone {
    align-items: center;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 7px;
    outline: none;
    padding: 4px 20px;
    text-align: center;
    width: 100%;

    &:focus-visible {
      border-radius: 8px;
      box-shadow: 0 0 0 2px #0078d7;
    }
  }

  &__inputs {
    display: flex;
    gap: 10px;
    width: 100%;
  }

  &__input {
    flex: 1;
    min-width: 0;
  }

  &__error {
    color: #bd1015 !important;
  }

  &__files {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

}

.scanning-file {
  background: #fff;
  border: 1px solid #d8d2bd;
  border-left: 4px solid #767c82;
  border-radius: 4px;
  padding: 9px 11px;

  &--processing {
    border-left-color: #0078d7;
  }

  &--accepted {
    border-left-color: #24851a;
  }

  &--rejected,
  &--error {
    border-left-color: #c80d0d;
  }

  &__header {
    align-items: center;
    display: flex;
    justify-content: space-between;

    > div {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    strong {
      overflow-wrap: anywhere;
    }

    span {
      color: #767c82;
      font-size: 11px;
    }

    .scanning-file__error {
      color: #c80d0d;
      line-height: 1.3;
      margin-top: 3px;
    }
  }

  &__remove {
    background: transparent;
    border: 0;
    color: #767c82;
    cursor: pointer;
    height: fit-content;
    font-size: 22px;
    width: fit-content;
  }

  &__report-button {
    align-self: flex-start;
    height: auto;
    margin-left: 0;
    margin-top: 9px;
    min-height: 36px;
    padding: 8px 14px;
    width: auto;
  }

}

.scanning-file-group {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &--accepted .scanning-file-group__title {
    color: #24851a;
  }

  &--failed .scanning-file-group__title {
    color: #c80d0d;
  }
}

.scanning-file-group__title {
  align-items: center;
  color: #767c82;
  display: flex;
  font-size: 14px;
  gap: 7px;
  margin: 7px 0 0;

  span {
    align-items: center;
    border: 1px solid currentColor;
    border-radius: 10px;
    display: inline-flex;
    font-size: 10px;
    height: 18px;
    justify-content: center;
    min-width: 18px;
    padding: 0 5px;
  }
}

@media screen and (max-width: 700px) {
  .scanning {
    padding: 10px;

    &__content {
      padding: 15px;
    }

    &__inputs {
      flex-direction: column;
    }
  }
}
</style>
